import React, { useState } from 'react'
import styled from 'styled-components'
import { useRecoilValue } from 'recoil'
import { BaseTable, Checkbox, TooltipAnchor } from '@gnomad/ui'
import { Badge } from '@gnomad/ui'

import { analysisIdAtom, locusMafAtom } from '../sharedState'
import {
  geneYellowThreshold,
  yellowThresholdColor,
  geneGreenThreshold,
  greenThresholdColor,
  RoundedNumber,
  formatMacCount,
  MAC_CASES_TOOLTIP,
  MAC_CONTROLS_TOOLTIP,
  MAC_TOTAL_TOOLTIP,
} from '../PhenotypeList/Utils'
import { GeneAssociations, AnalysisMetadata } from '../types'
import { MembershipFilterOptions } from '../variantState'
import { LocusGeneContextMenu } from '../Manhattan/components/LocusGeneContextMenu'
import { geneMacColumnMode } from '../geneAssociationSemantics'
import { isBurdenAnnotationSelected, setBurdenAnnotationSelected } from './burdenMembershipFilters'

const Table = styled(BaseTable)`
  min-width: 325px;

  .tooltip {
    text-decoration: underline;
    text-decoration-style: dashed;
    text-decoration-thickness: 1px;
    text-decoration-color: var(--theme-text-muted, grey);
  }
`

// Sort order for annotations
const annotationOrder: Record<string, number> = {
  'pLoF': 1,
  'pLoF;missenseLC': 2,
  'missenseLC': 3,
  'synonymous': 4,
}

// Sort and prepare data for the new table format
export const prepareAllMafTableData = (geneAssociations: GeneAssociations[]) => {
  // Filter to standard annotations and sort by annotation then MAF
  return geneAssociations
    .filter(g => ['pLoF', 'pLoF;missenseLC', 'missenseLC', 'synonymous'].includes(g.annotation))
    .sort((a, b) => {
      const annotationDiff = (annotationOrder[a.annotation] || 99) - (annotationOrder[b.annotation] || 99)
      if (annotationDiff !== 0) return annotationDiff
      // Sort by MAF descending (1% first, then 0.1%, then 0.01%)
      return (b.max_maf || 0) - (a.max_maf || 0)
    })
}

interface Props {
  geneAssociations: GeneAssociations[]
  analysisMetadata?: AnalysisMetadata
  membershipFilters?: MembershipFilterOptions
  setMembershipFilters?: (filters: MembershipFilterOptions) => void
}

const allGeneQcPass = true // TODO: fix me

export const GeneQc: React.FC<{ geneAssociations: GeneAssociations[]; burdenSet: string }> = ({
  geneAssociations,
  burdenSet,
}) => {
  const qcItems = [
    { field: 'keep_gene_coverage', message: 'Coverage less than 20X' },
    // { field: 'keep_gene_caf', message: 'total_variants less than 1e-4' },
    { field: 'keep_gene_n_var', message: 'Number of variants less than 2' },
  ]

  // @ts-expect-error ts-migrate(2322) FIXME
  const qcResults: any = ([] = qcItems.reduce((acc, qcItem) => {
    let pass

    const item = geneAssociations.find((r) => r.annotation == burdenSet) as any

    if (item && item[qcItem.field]) {
      pass = item[qcItem.field] as boolean
    }

    if (!pass) {
      return [...acc, qcItem.message]
    }
    return []
  }, []))

  let geneQcFlag: any = null

  if (allGeneQcPass || qcResults.length === 0) {
    const message = <Badge level='success'>Pass</Badge>
    const tooltip = 'All Gene QC checks passed'

    geneQcFlag = (
      <TooltipAnchor tooltip={tooltip}>
        <span>{message}</span>
      </TooltipAnchor>
    )
  } else {
    const message = <Badge level='warning'>Fail</Badge>
    const tooltip = `Reason: ${qcResults.join(', ')}`

    geneQcFlag = (
      <TooltipAnchor tooltip={tooltip}>
        <span>{message}</span>
      </TooltipAnchor>
    )
  }

  return <>{geneQcFlag}</>
}

// Display label for annotations
const annotationLabel: Record<string, string> = {
  'pLoF': 'pLoF',
  'pLoF;missenseLC': 'pLoF + missenseLC',
  'missenseLC': 'missenseLC',
  'synonymous': 'synonymous',
}

export const GeneBurdenTable = ({
  geneAssociations,
  analysisMetadata,
  membershipFilters,
  setMembershipFilters,
}: Props) => {
  const selectedMaf = useRecoilValue(locusMafAtom)
  const macColumnMode = geneMacColumnMode(analysisMetadata?.trait_type)

  // Prepare all rows sorted by annotation then MAF, keeping only one row per annotation
  // (deduplicate by annotation, keeping the first/most significant one)
  const allRows = React.useMemo(() => {
    const filtered = prepareAllMafTableData(geneAssociations).filter(row => row.max_maf === selectedMaf)
    const seen = new Set<string>()
    return filtered.filter(row => {
      if (seen.has(row.annotation)) return false
      seen.add(row.annotation)
      return true
    })
  }, [geneAssociations, selectedMaf])

  // Render a p-value cell with highlighting
  const renderPvalueCell = (value: number | null | undefined) => {
    if (value === null || value === undefined) return '-'
    let highlightColor = value < geneYellowThreshold ? yellowThresholdColor : null
    highlightColor = value < geneGreenThreshold ? greenThresholdColor : highlightColor
    return <RoundedNumber num={value} highlightColor={highlightColor} />
  }

  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; gene: GeneAssociations } | null>(null);
  const currentAnalysisId = useRecoilValue(analysisIdAtom);

  // Get gene info from first row (all rows have same gene)
  const geneInfo = allRows[0] ? {
    geneId: allRows[0].gene_id,
    geneSymbol: allRows[0].gene_symbol,
    contig: allRows[0].contig,
    start: allRows[0].gene_start_position || undefined,
    relationship: 'implicated' as const,
  } : null;

  return (
    <div>
      <Table>
        <thead>
          <tr>
            <th scope='col'>Category</th>
            <th scope='col'>P-value SKATO</th>
            <th scope='col'>P-value burden</th>
            <th scope='col'>P-value SKAT</th>
            {macColumnMode === 'case-control' && (
              <>
                <th scope='col' title={MAC_CASES_TOOLTIP}>MAC cases</th>
                <th scope='col' title={MAC_CONTROLS_TOOLTIP}>MAC controls</th>
              </>
            )}
            {macColumnMode === 'total' && (
              <th scope='col' title={MAC_TOTAL_TOOLTIP}>MAC total</th>
            )}
            {membershipFilters && <th scope='col'>Filter</th>}
          </tr>
        </thead>
        <tbody>
          {allRows.map((row, idx) => (
            <tr
              key={`${row.annotation}-${row.max_maf}`}
              onContextMenu={(e) => {
                e.preventDefault();
                setContextMenu({ x: e.clientX, y: e.clientY, gene: row });
              }}
              style={{ cursor: 'context-menu' }}
            >
              <th>{annotationLabel[row.annotation] || row.annotation}</th>
              <td>{renderPvalueCell(row.pvalue)}</td>
              <td>{renderPvalueCell(row.pvalue_burden)}</td>
              <td>{renderPvalueCell(row.pvalue_skat)}</td>
              {macColumnMode === 'case-control' && (
                <>
                  <td>{formatMacCount(row.mac_case)}</td>
                  <td>{formatMacCount(row.mac_control)}</td>
                </>
              )}
              {macColumnMode === 'total' && <td>{formatMacCount(row.mac)}</td>}
              {membershipFilters && (
                <td>
                  <Checkbox
                    label=''
                    checked={isBurdenAnnotationSelected(row.annotation, membershipFilters)}
                    id={`${row.annotation}-${row.max_maf}-membership`}
                    disabled={false}
                    onChange={(checked: boolean) => {
                      setMembershipFilters?.(
                        setBurdenAnnotationSelected(row.annotation, checked, membershipFilters)
                      )
                    }}
                  />
                </td>
              )}
            </tr>
          ))}
          {allRows.length === 0 && (
            <tr>
              <td colSpan={4 + (macColumnMode === 'case-control' ? 2 : macColumnMode === 'total' ? 1 : 0) + (membershipFilters ? 1 : 0)} style={{ textAlign: 'center', color: 'var(--theme-text-muted, #666)' }}>
                No burden test results available
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      {contextMenu && geneInfo && (
        <LocusGeneContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          gene={geneInfo}
          currentPhenotypeDescription={currentAnalysisId || undefined}
          onClose={() => setContextMenu(null)}
        />
      )}
    </div>
  )
}
export default GeneBurdenTable
